/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV-1) - Initial creation of the LogViewerPage component.
 *                                   - Implemented auto-refresh every 1 second for '/v1/chat?getlog=1'.
 *                                   - Displays raw JSON response.
 * 1.0.1:gemini-1.5-flash (PREV) - Modified `handleSendMessage` to include the last 20 messages (user and assistant)
 *                                   as `conversation_history` in the payload sent to the `/chat` endpoint.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Added an input field for the Gemini API key at the top of the page.
 *                                   - Implemented `onChange` event to save the Gemini API key to IndexedDB settings.
 *                                   - Refactored IndexedDB interactions to use `ui/chat/src/utils/indexedDB.ts`.
 *
 * -----
 * SECURITY_RISK: MEDIUM - This component fetches and displays log data and now allows input of a Gemini API key.
 *                         The API key is stored locally in IndexedDB and sent as a header to the backend.
 *                         While storage is local, the key is sensitive.
 * INTENT: Provide a simple, auto-refreshing page to monitor chat logs for debugging or observation,
 *         and allow users to configure their Gemini API key for AI assistant functionality.
 * PROMPTER_INSTRUCTIONS: Ensure the refresh interval is configurable if needed in the future.
 *                        Consider adding basic formatting or filtering if the log output becomes complex.
 *                        Ensure the Gemini API key input is secure (e.g., `type="password"`) and saved correctly.
 * LLM_CONFIDENCE: 95% - Standard React component with `useEffect` and `setTimeout` for data fetching.
 *                        IndexedDB integration for the API key is robust.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiClient, ApiError } from '../api/apiClient.tsx';
import { Spinner } from '../components/ui/spinner.tsx';
import { displayGlobalError } from '../utils/globalErrorHandler.ts';
import { Input } from '../components/ui/input.tsx';
import { Label } from '@radix-ui/react-label';
import { getSettings, saveSetting } from '../utils/indexedDB.ts'; // NEW: Import from unified indexedDB.ts

const LogViewerPage: React.FC = () => {
  const [logData, setLogData] = useState<any>(null); // 'any' is acceptable here as we're displaying raw log
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [geminiApiKey, setGeminiApiKey] = useState<string>(''); // NEW: State for Gemini API Key
  const [geminiApiKeyLoading, setGeminiApiKeyLoading] = useState<boolean>(true); // NEW: State for loading Gemini API Key

  // NEW: Effect to load Gemini API key from IndexedDB on mount
  useEffect(() => {
    const loadGeminiApiKey = async () => {
      setGeminiApiKeyLoading(true);
      try {
        const settings = await getSettings(); // Use the unified getSettings
        setGeminiApiKey(settings.gemini_key || '');
      } catch (e) {
        console.error("Failed to load Gemini API key from IndexedDB:", e);
        // Optionally, display a local error or global error
      } finally {
        setGeminiApiKeyLoading(false);
      }
    };
    loadGeminiApiKey();
  }, []);

  // NEW: Handler for Gemini API key input change
  const handleGeminiApiKeyChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newKey = e.target.value;
    setGeminiApiKey(newKey);
    try {
      await saveSetting('gemini_key', newKey); // Use the unified saveSetting
      // No alert, silent save is preferred
    } catch (e) {
      console.error("Failed to save Gemini API key to IndexedDB:", e);
      displayGlobalError("Failed to save Gemini API key. Please try again.", 0);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const recursiveFetch = async () => {
      if (!isMounted) return; // Stop if component is unmounted

      setLoading(true);
      setError(null);
      try {
        const response = await apiClient<any>('/chat?getlog=1', { bypassCache: true });
        if (isMounted) { // Check again before setting state
          setLogData(response.data);
        }
      } catch (err: unknown) {
        if (isMounted) { // Check again before setting state
          console.log(err);
          const errorMessage = err instanceof ApiError ? err.message : 'An unexpected error occurred.';
          setError(`Failed to fetch log: ${errorMessage}`);
          displayGlobalError(`Failed to fetch log: ${errorMessage}`, (err as ApiError)?.status || 0);
          setLogData(null);
        }
      } finally {
        if (isMounted) { // Check again before setting state
          setLoading(false);
          setTimeout(recursiveFetch, 5000);
        }
      }
    };

    recursiveFetch(); // Start the fetching

    return () => {
      isMounted = false; // Cleanup: prevent further fetches and state updates
    };
  }, []); // Empty dependency array

  return (
    <div className="p-4">
      <h2 className="text-2xl font-semibold text-secondary-foreground mb-4">Chat Log Viewer</h2>

      {/* NEW: Gemini API Key Input */}
      <div className="mb-6 p-4 border rounded-md bg-gray-50">
        <Label htmlFor="gemini-api-key" className="block text-sm font-medium text-foreground mb-1">
          Gemini API Key (Optional)
        </Label>
        <div className="flex items-center space-x-2">
          <Input
            id="gemini-api-key"
            type="password" // Use type="password" for sensitive keys
            placeholder="Enter your Gemini API Key"
            value={geminiApiKey}
            onChange={handleGeminiApiKeyChange}
            disabled={geminiApiKeyLoading}
            className="flex-grow"
          />
          {geminiApiKeyLoading && <Spinner size="sm" />}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          This key is saved locally in your browser's IndexedDB and sent as an `X-GAPI-Key` header.
        </p>
      </div>

      <p className="text-muted-foreground mb-4">
        Refreshing log data every 5 seconds {loading && (<code>Refreshing...</code>)}.
      </p>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive p-3 rounded-md mb-4">
          <p className="font-semibold">Error:</p>
          <p>{error}</p>
        </div>
      )}

      {logData && !error && (
        <div className="bg-gray-800 text-white p-4 rounded-md overflow-x-auto font-mono text-xs">
          <pre>{logData}</pre>
        </div>
      )}

      {!logData && !error && (
        <p className="text-muted-foreground">No log data available.</p>
      )}
    </div>
  );
};

export default LogViewerPage;